import clsx from "clsx";
import { Building2Icon, GraduationCapIcon, MapPinIcon } from "lucide-react";

import {
  SheetSectionContainer,
  SheetSectionEmptyText,
  SheetSectionHeader,
} from "@/components";

import { dates } from "@/lib/dates";
import { Education } from "@/types";

export const SheetEducations = ({
  educations,
  showResumeEducations,
}: {
  educations: Education[];
  showResumeEducations?: boolean;
}) => {
  // Filter out educations without school and degree.
  let filteredEducations = educations.filter(
    (education) => !!education.degree?.trim() || !!education.school?.trim(),
  );

  // Filter out educations that are not on the user's profile if applicable.
  filteredEducations = showResumeEducations
    ? filteredEducations
    : filteredEducations.filter((education) => !education.isResumeField);

  if (!filteredEducations.length) {
    return (
      <SheetSectionContainer>
        <SheetSectionHeader
          icon={GraduationCapIcon}
          title="Education"
          isEmpty
        />
        <SheetSectionEmptyText text="This candidate hasn't added any educational background." />
      </SheetSectionContainer>
    );
  }

  return (
    <SheetSectionContainer>
      <SheetSectionHeader icon={GraduationCapIcon} title="Educations" />
      <div>
        {filteredEducations.map((education: Education, i) => {
          const dateDisplay =
            dates.prepareDateDisplayForCertificationOrEducationOrWorkExperience(
              {
                startMonth: education.startMonth,
                startYear: education.startYear,
                endMonth: education.endMonth,
                endYear: education.endYear,
                current: education.current,
              },
            );

          return (
            <div className="flex gap-4" key={i}>
              <div className="flex flex-col items-center">
                <div className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full border border-slate-400 bg-slate-100">
                  <Building2Icon className="h-5 w-5 text-slate-500" />
                </div>
                {i !== filteredEducations.length - 1 &&
                  filteredEducations.length > 1 && (
                    <div className="my-1 h-4 h-full w-[1px] bg-slate-200" />
                  )}
              </div>
              <div
                className={clsx(
                  "flex w-full flex-col",
                  i !== filteredEducations.length - 1 &&
                    filteredEducations.length > 1 &&
                    "pb-6",
                )}
              >
                <div className="flex items-center justify-between">
                  <p className="font-medium text-slate-700">
                    {education.degree}
                  </p>
                  {dateDisplay && (
                    <p className="text-sm text-slate-500">{dateDisplay}</p>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-slate-600">
                    {education.school?.trim() ? (
                      education.school
                    ) : (
                      <span className="text-slate-400">
                        School name unspecified
                      </span>
                    )}
                  </p>
                  {education.location && (
                    <div className="flex items-center gap-1">
                      <MapPinIcon className="h-4 w-4 text-slate-500" />
                      <p className="text-sm text-slate-500">
                        {education.location}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </SheetSectionContainer>
  );
};
